
import React, { useState, useEffect } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  Box,
  Button,
  IconButton,
  TextField,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import FlagCircleRoundedIcon from "@mui/icons-material/FlagCircleRounded";
import { useNavigate, useSearchParams } from "react-router-dom";
import { axiosInstance } from "../../../utility/apiInstance";
import toast from "react-hot-toast";
import OceanTrackingHeader from "../../../components/OceanTracking/OceanTrackingHeader";
import OceanEventLine from "../../../components/OceanTracking/OceanEventLine";
import OceanTableView from "../../../components/OceanTracking/OceanTableView";
import TabularViewofFlagList, { FlagType } from "../../../components/TabularViewofFlagList";
import { FirstAndLastType } from "./types/type";

const defaultTheme = createTheme();

function OceanTrackingPage() {
  const [searchparams] = useSearchParams();
  const params = searchparams.get("trackingId");

  const [oceanTrackList, setOceanTrackList] = useState<any>(null);
  const [vesselDetail, setVesselDetail] = useState<any[]>([]);
  const [billOfLadingUpdates, setBillOfLadingUpdates] = useState<any>({});
  const [billOfLadingUpdatesForTable, setBillOfLadingUpdatesForTable] = useState<any[]>([]);
  const [containerDataUpdate, setContainerDataUpdate] = useState<any[]>([]);
  const [showBox, setShowBox] = useState(false);
  const [publicUrl, setPublicUrl] = useState<any>(null);
  const [selectedContainer, setSelectedContainer] = useState<string>("All");
  const [firstAndLast, setFirstAndLast] = useState<FirstAndLastType>({
  first: [],
  last: [],
});
  const [flag, setFlag] = useState<FlagType[]>([]);
  const [isFlag, setIsFlag] = useState(false);
  const [selectedContainer1, setSelectedContainer1] = useState("Bill of Lading");

  const navigate = useNavigate();

  const handleRedirect = () => {
    navigate("/tracking-management");
  };

  async function oceanTrackedList() {
    try {
      const response = await axiosInstance.get(
        `/api/v1/ocean/traking-details/${params}`
      );

      if (response.data.success) {
        const {
          header,
          billOfLadingData,
          billOfLadingDataForTable,
          containerData,
          first,
          last,
          shipmentFlagsDetails,
        } = response.data.data;

        // ✅ header — safe fallback
        setOceanTrackList(header || null);
        setVesselDetail(header?.vessel || []);

        // ✅ billOfLadingData — always set as object with ports array
        setBillOfLadingUpdates(
          billOfLadingData && typeof billOfLadingData === "object" && !Array.isArray(billOfLadingData)
            ? billOfLadingData
            : { ports: [] }
        );

        // ✅ billOfLadingDataForTable — always array
        setBillOfLadingUpdatesForTable(
          Array.isArray(billOfLadingDataForTable) ? billOfLadingDataForTable : []
        );

        // ✅ containerData — always array
        const safeContainerData = Array.isArray(containerData) ? containerData : [];
        setContainerDataUpdate(safeContainerData);

        // ✅ firstAndLast — safe fallback
        setFirstAndLast({
          first: Array.isArray(first) ? first : [],
          last: Array.isArray(last) ? last : [],
        });

        // ✅ flags — safe fallback
        setFlag(Array.isArray(shipmentFlagsDetails) ? shipmentFlagsDetails : []);

        // ✅ Default container selection — only if containers exist and first key is valid
        if (safeContainerData.length > 0) {
          const firstKey = Object.keys(safeContainerData[0])[0];
          setSelectedContainer(firstKey || "All");
        } else {
          setSelectedContainer("All");
        }

        // ✅ Pre-select view type based on tracking type
        if (header?.type === "CT") {
          setSelectedContainer1("Container");
        } else {
          setSelectedContainer1("Bill of Lading");
        }
      }
    } catch (error: any) {
      const apiError = error?.response?.data?.error || "Something went wrong.";
      setTimeout(() => {
        toast.dismiss();
        toast.error(apiError);
      }, 500);
      handleRedirect();
    }
  }

  useEffect(() => {
    if (params) {
      oceanTrackedList();
    }
  }, [params]);

  const handleContainerChange = (value: string) => {
    setSelectedContainer(value);
  };

  const handleContainerChange1 = (value: string) => {
    setSelectedContainer1(value);
  };

  const filteredContainers =
    selectedContainer === "All"
      ? containerDataUpdate
      : containerDataUpdate.filter(
          (container: any) => Object.keys(container)[0] === selectedContainer
        );

  const handleShareIconClick = async () => {
    setShowBox(!showBox);
    try {
      const { data } = await axiosInstance.get(
        `/api/v1/ocean/generate-public-link/${params}`
      );
      setPublicUrl(data?.data?.publicUrl);
    } catch (error) {}
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success("URL copied Successfully!");
    setShowBox(false);
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <div className="sharebox flex justify-end items-center mb-6">
        <h2 className="text-lg font-bold">Copy Link</h2>
        <figure className="relative bg-yellow-500 w-[32px] h-[32px] rounded-full ml-3">
          <IconButton onClick={handleShareIconClick} className="relative z-index-2">
            <ShareIcon className="iconshare text-white-500" />
          </IconButton>
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full left-0 bg-yellow-400 opacity-75"></span>
        </figure>
      </div>

      <div className="min-h-screen">
        <div className="grid grid-cols-12 gap-x-4">
          {/* Left column */}
          <div className="lg:col-span-7 col-span-12">
            <OceanTrackingHeader
              oceanTrackList={oceanTrackList}
              vesselDetail={vesselDetail}
            />

            <div className="whiteBox whiteBoxCustom">
              <div className="flex flex-col w-full">
                <div className="old_dashboard_title margin0 flex gap-3">
                  <h1 className="text-2xl font-bold text-gray-700 mt-[-15px] mb-[10px]">
                    List of Events
                  </h1>
                  {flag && flag.length >= 1 && (
                    <button
                      className="mt-[-15px] mb-[10px]"
                      onClick={() => setIsFlag((prev) => !prev)}
                    >
                      <FlagCircleRoundedIcon />
                    </button>
                  )}
                </div>

                <div className="flex gap-4">
                  <div className="mb-0 items-center bg-yellow-400 rounded-lg py-1 px-2 inline-block">
                    <select
                      className="pr-2 text-white bg-transparent border-none focus:outline-none cursor-pointer"
                      onChange={(e) => handleContainerChange1(e.target.value)}
                      value={selectedContainer1}
                    >
                      {oceanTrackList?.type !== "CT" && (
                        <option className="text-black" value="Bill of Lading">
                          Bill of Lading
                        </option>
                      )}
                      <option className="text-black" value="Container">
                        Container
                      </option>
                    </select>
                  </div>

                  {selectedContainer1 === "Container" && (
                    <div className="mb-0 items-center bg-yellow-400 rounded-lg py-2 px-2 inline-block">
                      <div className="flex items-center">
                        <i className="border-r-1 border-gray-400">
                          <img
                            src="/images/container2.svg"
                            className="w-[19px]"
                            alt="container"
                          />
                        </i>
                        <select
                          className="pl-2 text-white bg-transparent border-none focus:outline-none cursor-pointer"
                          onChange={(e) => handleContainerChange(e.target.value)}
                          value={selectedContainer}
                        >
                          <option className="text-black" value="All">
                            All
                          </option>
                          {containerDataUpdate.map((container: any, index: number) => {
                            const containerNumber = Object.keys(container)[0];
                            return (
                              <option className="text-black" key={index} value={containerNumber}>
                                {containerNumber}
                              </option>
                            );
                          })}
                          {containerDataUpdate.length === 0 && (
                            <option className="text-black" disabled>
                              No containers available
                            </option>
                          )}
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {isFlag ? (
                  <TabularViewofFlagList flags={flag} />
                ) : (
                  <OceanTableView
                    billOfLadingUpdatesForTable={billOfLadingUpdatesForTable}
                    oceanTrackList={oceanTrackList}
                    selectedContainer1={selectedContainer1}
                    selectedContainer={selectedContainer}
                    filteredContainers={filteredContainers}
                    firstAndLast={firstAndLast}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Right column */}
          <OceanEventLine
            billOfLadingUpdates={billOfLadingUpdates}
            containerDataUpdate={containerDataUpdate}
            selectedContainer={selectedContainer}
            selectedContainer1={selectedContainer1}
            firstAndLast={firstAndLast}
          />
        </div>
      </div>

      {showBox && (
        <div className="toolTips">
          <Box
            sx={{
              position: "absolute",
              top: "140px",
              right: "60px",
              height: "70px",
              width: { xs: "100%", sm: "200px" },
              backgroundColor: "lightgray",
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
              zIndex: 9999,
              padding: "10px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.5)",
            }}
          >
            <TextField
              value={publicUrl || ""}
              InputProps={{
                readOnly: true,
                style: { fontSize: "12px" },
              }}
              fullWidth
            />
            <Button
              onClick={handleCopy}
              sx={{
                ml: 1,
                height: "100%",
                fontSize: "12px",
                padding: "10px",
                color: "white",
                backgroundColor: "black",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              Copy url
            </Button>
          </Box>
        </div>
      )}
    </ThemeProvider>
  );
}

export default OceanTrackingPage;